import { ConnectButton } from '@rainbow-me/rainbowkit';
import { AppSidebar } from "@/components/app-sidebar";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/breadcrumb";
import { Separator } from "@/components/separator";
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from "@/components/sidebar";
import { Link } from "react-router-dom";
import { RiInformationLine, RiLoader4Line } from "@remixicon/react";
import { AnimatedCounter } from "@/components/animated-counter";
import { useBlockchain } from "@/providers/global-data.provider";
import { EmissionChart } from "@/components/emission-chart";
import { EmissionScheduleTable } from "@/components/emission-schedule-table";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/tabs";
import { RPCDetails } from "@/components/rpc-details";

export default function BlockchainEmissionPage() {
  const {
    currentBlockReward,
    currentBlock,
    circulatingSupply,
    maxSupply,
    isLoading,
    error
  } = useBlockchain();

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <div className="px-4 md:px-6 lg:px-8 @container">
          <div className="w-full max-w-6xl mx-auto">
            <header className="flex flex-wrap gap-3 min-h-20 py-4 shrink-0 items-center transition-all ease-linear border-b">
              <div className="flex flex-1 items-center gap-2">
                <SidebarTrigger className="-ms-1" />
                <div className="max-lg:hidden lg:contents">
                  <Separator
                    orientation="vertical"
                    className="me-2 data-[orientation=vertical]:h-4"
                  />
                  <Breadcrumb>
                    <BreadcrumbList>
                      <BreadcrumbItem className="hidden md:block">
                        <BreadcrumbLink asChild>
                          <Link to="/">Home</Link>
                        </BreadcrumbLink>
                      </BreadcrumbItem>
                      <BreadcrumbSeparator className="hidden md:block" />
                      <BreadcrumbItem>
                        <BreadcrumbLink asChild>
                          <Link to="/blockchain">Blockchain</Link>
                        </BreadcrumbLink>
                      </BreadcrumbItem>
                      <BreadcrumbSeparator className="hidden md:block" />
                      <BreadcrumbItem>
                        <BreadcrumbPage>Emission</BreadcrumbPage>
                      </BreadcrumbItem>
                    </BreadcrumbList>
                  </Breadcrumb>
                </div>
              </div>
              <ConnectButton />
            </header>

            <div className="py-6">
              <div className="flex items-center mb-6">
                <div className="w-1 h-8 bg-[#FF8A3C] mr-3"></div>
                <h1 className="text-3xl font-bold">Emission Schedule</h1>
              </div>

              <p className="text-muted-foreground mb-8 max-w-3xl">
                The Larissa blockchain follows a carefully designed emission schedule that spans 16 years.
                Block rewards are reduced over time to create a balanced and sustainable economic model.
              </p>

              <div className="w-full max-w-xs h-1 bg-gradient-to-r from-[#FF8A3C] to-transparent mb-8 rounded-full"></div>

              <div className="mb-8">
                <Card className="bg-gradient-to-r from-[#FF8A3C]/10 to-transparent border-l-4 border-l-[#FF8A3C]">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground mb-1">Current Block Height</p>
                        {isLoading ? (
                          <div className="flex items-center">
                            <RiLoader4Line className="size-6 text-[#FF8A3C] animate-spin mr-2" />
                            <span className="text-2xl font-bold">Loading...</span>
                          </div>
                        ) : error ? (
                          <p className="text-2xl font-bold text-red-500">Error loading block</p>
                        ) : (
                          <p className="text-3xl font-bold text-[#FF8A3C]">
                            #{currentBlock.toLocaleString()}
                          </p>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-muted-foreground mb-1">Current Block Reward</p>
                        {isLoading ? (
                          <div className="flex items-center justify-end">
                            <RiLoader4Line className="size-6 text-[#FF8A3C] animate-spin" />
                          </div>
                        ) : error ? (
                          <p className="text-2xl font-bold text-red-500">--</p>
                        ) : (
                          <p className="text-3xl font-bold">
                            {currentBlockReward.toFixed(4)} LRS
                          </p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                <Card className="bg-sidebar border border-border">
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <span>Total Mined Emission</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-col items-center justify-center h-32">
                      {isLoading ? (
                        <div className="flex flex-col items-center">
                          <RiLoader4Line className="size-8 animate-spin" />
                          <div className="text-sm text-muted-foreground mt-2">Loading...</div>
                        </div>
                      ) : error ? (
                        <div className="text-sm text-red-500">Error loading data</div>
                      ) : (
                        <>
                          <div className="text-4xl font-bold">
                            <AnimatedCounter
                              value={circulatingSupply}
                              updateInterval={2000}
                              incrementAmount={1.5}
                            /> LRS
                          </div>
                          <div className="text-sm text-muted-foreground mt-2">
                            Out of {maxSupply.toLocaleString()} LRS (Max Supply)
                          </div>
                        </>
                      )}
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-sidebar border border-border">
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <span>Circulating Supply</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-col items-center justify-center h-32">
                      {isLoading ? (
                        <div className="flex flex-col items-center">
                          <RiLoader4Line className="size-8 animate-spin" />
                          <div className="text-sm text-muted-foreground mt-2">Loading...</div>
                        </div>
                      ) : error ? (
                        <div className="text-sm text-red-500">Error loading data</div>
                      ) : (
                        <>
                          <div className="text-4xl font-bold text-[#FF8A3C]">
                            {((circulatingSupply / maxSupply) * 100).toFixed(2)}%
                          </div>
                          <div className="text-sm text-muted-foreground mt-2">
                            {circulatingSupply.toLocaleString()} / {maxSupply.toLocaleString()} LRS
                          </div>
                        </>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>

              <Tabs defaultValue="chart" className="mb-8">
                <TabsList className="mb-6">
                  <TabsTrigger value="chart">Emission Chart</TabsTrigger>
                  <TabsTrigger value="table">Emission Schedule</TabsTrigger>
                  <TabsTrigger value="rpc">RPC Details</TabsTrigger>
                </TabsList>

                <TabsContent value="chart">
                  <div className="mb-8">
                    <div className="flex items-center mb-4">
                      <div className="w-1 h-6 bg-[#FF8A3C] mr-3"></div>
                      <h2 className="text-2xl font-bold">Continuously Decaying Block Reward (Years 2-16)</h2>
                    </div>
                    <Card className="bg-sidebar border border-border p-4">
                      <CardContent className="p-0">
                        <EmissionChart />
                      </CardContent>
                    </Card>
                  </div>
                </TabsContent>

                <TabsContent value="table">
                  <div className="mb-8">
                    <div className="flex items-center mb-4">
                      <div className="w-1 h-6 bg-[#FF8A3C] mr-3"></div>
                      <h2 className="text-2xl font-bold">Emission Schedule Table (Years 2-16)</h2>
                    </div>
                    <Card className="bg-sidebar border border-border overflow-hidden">
                      <CardContent className="p-0">
                        <EmissionScheduleTable />
                      </CardContent>
                    </Card>
                  </div>
                </TabsContent>

                <TabsContent value="rpc">
                  <div className="mb-8">
                    <div className="flex items-center mb-4">
                      <div className="w-1 h-6 bg-[#FF8A3C] mr-3"></div>
                      <h2 className="text-2xl font-bold">RPC Connection Details</h2>
                    </div>
                    <Card className="bg-sidebar border border-border">
                      <CardContent className="p-6">
                        <RPCDetails />
                      </CardContent>
                    </Card>
                  </div>
                </TabsContent>
              </Tabs>

              <div className="mt-10">
                <div className="flex items-center mb-4">
                  <div className="w-1 h-6 bg-[#FF8A3C] mr-3"></div>
                  <h2 className="text-2xl font-bold">Emission Algorithm</h2>
                </div>
                <Card className="bg-sidebar border border-border">
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <RiInformationLine className="mr-2 size-5 text-muted-foreground" />
                      <span>Block Reward Calculation</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground mb-4">
                      The Larissa blockchain uses a sophisticated algorithm to calculate block rewards. The rewards follow a reduction schedule that ensures a balanced emission over time:
                    </p>
                    <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                      <li>After block 639,675, a weekly reduction of 0.62% is applied</li>
                      <li>After block 1,952,106, rewards are split: 80% to miners, 14% to staking, and 6% to buyback fund</li>
                      <li>After block 5,466,756, rewards are split: 47% to Miners, 18% to Staking, and 7% to LRSNode, and 25% to Silo Node, and 3% to Liquidity Farming</li>
                    </ul>
                    <div className="mt-6 p-4 bg-muted/20 rounded-md">
                      <p className="text-sm text-muted-foreground">
                        The development fund receives a separate reward that also follows a reduction schedule, starting at 1 LRS per block for the first 2,000,000 blocks, then reducing by 0.62% weekly.
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}
